import {
  isValidPhoneNumber as isValidNumber,
  parsePhoneNumberFromString
} from 'libphonenumber-js/min'
import { useTranslation } from 'next-i18next'
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
  type RegisterOptions
} from 'react-hook-form'
import BaseTextField from 'src/components/Inputs/BaseTextField'
import { formatPhoneInput } from 'src/hooks/usePhoneInput'

/**
 * A person's contact number is never toll-free / premium-rate / shared-cost.
 * When a "+1" number is one of these AND its digits are also a valid Mexican
 * number, it is almost certainly a Mexican lada that lost its +52 — the NANP
 * toll-free codes 833 / 844 / 866 collide exactly with Tampico / Saltillo /
 * Monclova. That conjunction is the only case precise enough to offer a
 * one-tap fix; plain `isValidNumber('+52' + digits)` is NOT, because
 * libphonenumber accepts nearly any 10 digits as Mexican, so it would fire on
 * every legitimate US number.
 */
const NON_PERSONAL_TYPES = new Set(['TOLL_FREE', 'PREMIUM_RATE', 'SHARED_COST'])

/**
 * Catches the "+1 844 …" trap: `applyAutoPrefix` turns any 11-digit string
 * starting with 1 into a US/Canada number, so a Mexican number typed with a
 * stray leading 1 silently becomes a *valid* NANP number and sails past
 * `isValidNumber`.
 *
 * Two tiers, both advisory — never a block, since SpotMe has US and foreign
 * renters: every non-Mexican number gets a "confirm this" nudge, and only the
 * high-confidence conjunction above also gets a one-tap +52 swap.
 */
function foreignNumberHint(
  value: string
): { callingCode: string; suggestion?: string } | null {
  const parsed = parsePhoneNumberFromString(value)
  if (!parsed?.isValid() || parsed.country === 'MX') return null
  const type = parsed.getType()
  const mxTwin = `+52${parsed.nationalNumber}`
  const suggestion =
    type && NON_PERSONAL_TYPES.has(type) && isValidNumber(mxTwin)
      ? mxTwin
      : undefined
  return { callingCode: parsed.countryCallingCode, suggestion }
}

type Props<T extends FieldValues> = {
  name: Path<T>
  inputId?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<T> | any
  rules?: Omit<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>
  topLabel?: string
  placeholder?: string
  required?: boolean
  fullWidth?: boolean
  className?: string
  autoFocus?: boolean
  autoComplete?: string
  /** Country dial code without the "+" — used to auto-prefix bare digit input
   * (so MX users can type "5512345678" and get "+52 55 1234 5678"). Users
   * from other countries just type "+XX …" themselves. Default "52" (Mexico). */
  defaultCountryCode?: string
  /** Hide the helper text below the input (default false). */
  hideHelper?: boolean
  /**
   * Suppress the red border + inline error until the user has blurred the
   * field at least once. Use in multi-step flows where a prior validation
   * pass (silent submit, `trigger`, server-side `setError`, back-then-
   * forward navigation) has already populated `fieldState.error` and would
   * otherwise flash red the moment the step mounts.
   */
  hideErrorUntilTouched?: boolean
  /**
   * Show a non-blocking "¿quisiste decir +52 …?" suggestion when the number
   * parses as foreign but its national digits are also a valid Mexican
   * number. Opt-in: use it on internal/admin surfaces where a mistyped renter
   * phone is expensive. Never blocks submit — foreign numbers stay valid.
   */
  suggestMxAlternative?: boolean
}

/**
 * RHF-integrated phone input. The input ALWAYS shows the full formatted
 * international number — there is no country chip / flag selector, because
 * SpotMe accepts numbers from any country and a country chip inevitably
 * lies mid-typing for prefixes the parser can't yet commit to.
 *
 * Behaviour:
 *  - MX/US users can type bare digits — `formatPhoneInput` auto-prefixes
 *    with `defaultCountryCode` ("52" by default) so "5512345678" becomes
 *    "+52 55 1234 5678" in the input.
 *  - Anyone else types their own "+XX …" — the formatter just spaces it
 *    nicely via libphonenumber's `AsYouType`.
 *  - RHF always stores the full formatted value (e.g. `+52 55 1234 5678`,
 *    `+33 6 12 34 56 78`).
 */
export default function PhoneField<T extends FieldValues>({
  name,
  inputId,
  control,
  rules,
  topLabel,
  placeholder,
  required,
  fullWidth,
  className,
  autoFocus,
  autoComplete = 'tel',
  defaultCountryCode = '52',
  hideHelper,
  hideErrorUntilTouched,
  suggestMxAlternative
}: Props<T>) {
  const { t: tCommon } = useTranslation('common')
  const resolvedInputId = inputId ?? `phone-${name}`
  const descriptionId = `${resolvedInputId}-description`

  // Merge a phone-format validator on top of caller-supplied rules. If the
  // caller passes their own `validate` (rare), we run it first so a
  // domain-specific failure wins over the generic format hint.
  type ValidateFn = (
    value: unknown,
    formValues: unknown
  ) => boolean | string | Promise<boolean | string>
  const callerValidate =
    rules?.validate && typeof rules.validate === 'function'
      ? (rules.validate as unknown as ValidateFn)
      : undefined
  const mergedRules: RegisterOptions = {
    ...rules,
    validate: async (value: unknown, formValues: unknown) => {
      if (callerValidate) {
        const result = await callerValidate(value, formValues)
        if (result !== true) return result
      }
      const stringValue = (value as string | undefined) ?? ''
      if (!stringValue) return true
      return isValidNumber(stringValue) || tCommon('FORMS.INVALID_PHONE_HINT')
    }
  }

  const { field, fieldState } = useController({
    name,
    control,
    rules: mergedRules
  })

  const stringValue = (field.value as string | undefined) ?? ''
  // Normalize for display: auto-prefix bare digits with the default country
  // code, then run AsYouType for spacing. Idempotent — a prefilled
  // "+14156198585" renders as "+1 415 619 8585" on first paint.
  const displayValue = stringValue
    ? formatPhoneInput(stringValue, defaultCountryCode)
    : ''

  const isInvalid = !!stringValue && !isValidNumber(stringValue)
  const showInlineError = fieldState.isTouched && isInvalid
  const rhfError = hideErrorUntilTouched
    ? fieldState.isTouched && !!fieldState.error
    : !!fieldState.error
  const error = rhfError || showInlineError
  const message =
    (rhfError ? fieldState.error?.message : '') ||
    (showInlineError ? tCommon('FORMS.INVALID_PHONE_HINT') : '')
  // Valid-but-possibly-wrong: the number parses fine, so no error — only a
  // dismissable nudge. Suppressed while a real error is showing.
  const foreignHint =
    suggestMxAlternative && !error ? foreignNumberHint(stringValue) : null

  return (
    <div className={fullWidth ? 'w-full' : undefined}>
      <BaseTextField
        id={resolvedInputId}
        name={name}
        topLabel={topLabel}
        type="tel"
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        fullWidth={fullWidth}
        className={className}
        required={required}
        placeholder={placeholder ?? '+52 55 1234 5678'}
        value={displayValue}
        error={error}
        slotProps={{
          htmlInput: {
            inputMode: 'tel',
            'aria-label': topLabel,
            'aria-describedby':
              (error && message) || (!error && !hideHelper)
                ? descriptionId
                : undefined,
            style: { fontSize: 16 }
          }
        }}
        onChange={(e) => {
          const formatted = formatPhoneInput(e.target.value, defaultCountryCode)
          field.onChange(formatted)
        }}
        onBlur={field.onBlur}
        inputRef={field.ref}
      />
      {!hideHelper && !error && (
        <p id={descriptionId} className="mt-1 text-xs text-ink-200">
          {tCommon('FORMS.PHONE_HELPER')}
        </p>
      )}
      {error && message && (
        <p id={descriptionId} className="mt-1 text-xs text-ink-700">
          {message}
        </p>
      )}
      {foreignHint && (
        <div className="mt-1 flex flex-col gap-1 md:flex-row md:items-center md:gap-2">
          <p className="text-xs text-ink-700">
            {tCommon('FORMS.PHONE_NOT_MX_HINT', {
              callingCode: foreignHint.callingCode
            })}
          </p>
          {foreignHint.suggestion && (
            <button
              type="button"
              className="self-start text-xs font-bold text-blue-800 underline hover:text-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 md:self-auto"
              onClick={() =>
                field.onChange(
                  formatPhoneInput(foreignHint.suggestion ?? '', '52')
                )
              }
            >
              {tCommon('FORMS.PHONE_USE_MX_CTA', {
                phone: formatPhoneInput(foreignHint.suggestion, '52')
              })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
